/**
 * React hooks for automatic signal capture.
 * Mount once at app root — covers session, page view, scroll depth.
 * All hooks are fire-and-forget; errors are swallowed to never break UI.
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackingService } from "./service";

// ── Session tracking — mount once in App.tsx ──────────────────────────────────
export function useSessionTracking(): void {
  const startedAt = useRef<number>(Date.now());
  const pagesVisited = useRef<string[]>([]);

  useEffect(() => {
    startedAt.current = Date.now();
    trackingService.sessionStart("web");

    const onUnload = () => {
      const duration = Date.now() - startedAt.current;
      trackingService.sessionEnd(duration, pagesVisited.current);
    };

    window.addEventListener("beforeunload", onUnload);
    return () => {
      window.removeEventListener("beforeunload", onUnload);
      onUnload();
    };
  }, []);

  // expose so usePageTracking can push into pagesVisited
  return;
}

// ── Page view tracking — mount once in App.tsx ────────────────────────────────
export function usePageTracking(): void {
  const location = useLocation();
  const prevPage = useRef<string>("");
  const enteredAt = useRef<number>(Date.now());

  useEffect(() => {
    const currentPage = location.pathname;
    const durationMs = Date.now() - enteredAt.current;

    // fire page_view with duration of PREVIOUS page
    if (prevPage.current) {
      trackingService.pageView(prevPage.current, currentPage, durationMs);
    }

    prevPage.current = currentPage;
    enteredAt.current = Date.now();
  }, [location.pathname]);

  // fire on unmount for the last page
  useEffect(() => {
    return () => {
      if (prevPage.current) {
        trackingService.pageView(prevPage.current, "", Date.now() - enteredAt.current);
      }
    };
  }, []);
}

// ── Scroll depth tracking — use on any page/component ─────────────────────────
// Fires at 25%, 50%, 75%, 90% thresholds (once per threshold per mount)
export function useScrollTracking(page?: string): void {
  const firedThresholds = useRef(new Set<number>());
  const resolvedPage = page ?? (typeof window !== "undefined" ? window.location.pathname : "");

  useEffect(() => {
    const THRESHOLDS = [0.25, 0.5, 0.75, 0.9];

    const onScroll = () => {
      const scrolled = window.scrollY + window.innerHeight;
      const total = document.documentElement.scrollHeight;
      if (total === 0) return;
      const pct = scrolled / total;

      for (const t of THRESHOLDS) {
        if (pct >= t && !firedThresholds.current.has(t)) {
          firedThresholds.current.add(t);
          trackingService.scrollDepth(resolvedPage, t);
        }
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [resolvedPage]);
}

